import { useState, type FormEvent } from "react";
import { Badge, Btn, ErrorNote, Field, Loading, Notice, Section } from "../components/kit";
import { api, UNCHANGED, type SMSSettings as Stored, type SMSView } from "../lib/api";
import { ago, formatDateTime } from "../lib/format";
import { useAction, useResource } from "../lib/hooks";

/** The SMS provider (REVE SMS) behind sign-in codes. */
export function SMSSettings() {
  const { data, error, reload } = useResource<SMSView>("/settings/sms");
  if (error?.status === 404) {
    return (
      <Section title="SMS">
        <Notice title="SMS is not available on this server.">This API was built without the SMS sender.</Notice>
      </Section>
    );
  }
  return (
    <Section title="SMS" aside={data && (data.ready ? <Badge tone="ok">Sending</Badge> : <Badge tone="warn">Not sending</Badge>)}>
      <div className="sheet">
        <p className="sheet-intro">
          The gateway that sends sign-in codes to phones, REVE SMS. Keys are sealed with the server's <code className="code">SECRETS_KEY</code> when saved and are never shown again.
        </p>
        <ErrorNote error={error} />
        {data ? <SMSForm key={data.settings?.updatedAt ?? "new"} view={data} onSaved={reload} /> : !error && <Loading />}
        {data && <TestMessage ready={data.ready} onSent={reload} />}
      </div>
      {data && data.messages.length > 0 && <MessageLog view={data} />}
    </Section>
  );
}

function SMSForm({ view, onSaved }: { view: SMSView; onSaved: () => void }) {
  const stored: Stored | null = view.settings;
  const [provider, setProvider] = useState(stored?.provider ?? view.providers[0]?.name ?? "");
  const [enabled, setEnabled] = useState(stored?.enabled ?? true);
  const [options, setOptions] = useState<Record<string, string>>(stored?.options ?? {});
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const { busy, error, run } = useAction();
  const d = view.providers.find((p) => p.name === provider);
  // A secret is kept only while the provider stays the same.
  const isStored = (name: string) => stored?.provider === provider && stored.secrets[name] === UNCHANGED;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    const out = await run(() => api("PUT", "/settings/sms", { provider, enabled, options, secrets }));
    if (out) {
      setSaved(true);
      onSaved();
    }
  };

  if (!d) return <Notice tone="warn">No SMS provider is available on this server.</Notice>;
  return (
    <form className="form" onSubmit={submit}>
      <div className="form-grid">
        <Field label="Provider">
          <select className="input" value={provider} onChange={(e) => setProvider(e.target.value)}>
            {view.providers.map((p) => (
              <option key={p.name} value={p.name}>
                {p.label}
                {p.development ? " (development only)" : ""}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Sending" hint={enabled ? "Codes are sent, and sign-in by phone is offered." : "Nothing is sent; sign-in by phone is hidden."}>
          <span className="segmented" role="radiogroup">
            <button type="button" role="radio" aria-checked={enabled} onClick={() => setEnabled(true)}>
              On
            </button>
            <button type="button" role="radio" aria-checked={!enabled} onClick={() => setEnabled(false)}>
              Off
            </button>
          </span>
        </Field>
        {d.options.map((f) => (
          <Field key={f.name} label={f.label + (f.required ? "" : " (optional)")} hint={f.help}>
            <input
              className="input"
              value={options[f.name] ?? ""}
              placeholder={f.default}
              required={f.required}
              onChange={(e) => setOptions((o) => ({ ...o, [f.name]: e.target.value }))}
            />
          </Field>
        ))}
        {d.secrets.map((f) => (
          <Field key={f.name} label={f.label} hint={isStored(f.name) ? "Stored. Leave empty to keep it, or type a new one to replace it." : f.help}>
            <input
              className="input code"
              type="password"
              autoComplete="off"
              value={secrets[f.name] ?? ""}
              placeholder={isStored(f.name) ? UNCHANGED : ""}
              required={f.required && !isStored(f.name)}
              onChange={(e) => setSecrets((s) => ({ ...s, [f.name]: e.target.value }))}
            />
          </Field>
        ))}
      </div>
      <ErrorNote error={error} />
      <div className="form-actions">
        {saved && <span className="muted small">Saved. Every server picks it up within a minute.</span>}
        {stored && <span className="muted small">Last saved {ago(stored.updatedAt)}</span>}
        <Btn variant="primary" type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save SMS settings"}
        </Btn>
      </div>
    </form>
  );
}

function TestMessage({ ready, onSent }: { ready: boolean; onSent: () => void }) {
  const [to, setTo] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const { busy, error, run } = useAction();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSent(null);
    const out = await run(() => api<{ to: string }>("POST", "/settings/sms/test", { to }));
    if (out) setSent(out.to);
    onSent();
  };
  return (
    <form className="inline-form" onSubmit={submit}>
      <Field label="Send a test message" hint={ready ? "Checks the keys and sender ID with a real message." : "Save working settings first."}>
        <span className="inline">
          <input className="input" type="tel" value={to} onChange={(e) => setTo(e.target.value)} placeholder="01712345678" required />
          <Btn type="submit" disabled={busy || !ready}>
            {busy ? "Sending…" : "Send test"}
          </Btn>
        </span>
      </Field>
      <ErrorNote error={error} />
      {sent && <Notice tone="ok">The provider accepted a message to {sent}.</Notice>}
    </form>
  );
}

function MessageLog({ view }: { view: SMSView }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>To</th>
            <th>Why</th>
            <th>Result</th>
            <th>When</th>
          </tr>
        </thead>
        <tbody>
          {view.messages.map((m) => (
            <tr key={m.id}>
              <td className="code">{m.to}</td>
              <td>{m.purpose.replace(/_/g, " ")}</td>
              <td>{m.status === "sent" ? <Badge tone="ok">Sent</Badge> : <span title={m.error}><Badge tone="danger">Failed</Badge> <span className="muted small">{m.error}</span></span>}</td>
              <td className="muted" title={formatDateTime(m.createdAt)}>
                {ago(m.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
